import type { MaybeRefOrGetter } from 'vue'
import type { BetterAuthClientPlugin } from 'better-auth/client'
import type { auth } from 'auth'
import { socialRedirectClient } from 'auth/src/social-redirect-client-plugin'
import { computed, ref, watch, toValue } from 'vue'
import { createAuthClient } from 'better-auth/vue'
import {
    inferAdditionalFields,
    anonymousClient,
} from 'better-auth/client/plugins'
import {
    getSessionCookie,
    setSessionCookie,
    getAnonymousUserIdCookie,
    setAnonymousUserIdCookie,
    clearAnonymousUserIdCookie,
    BACKEND_URL,
    AUTH_BASE_PATH,
} from '~/utils'

function anonymousReauthClient() {
    return {
        id: 'anonymous-reauth' as const,
        getActions: ($fetch) => ({
            signIn: {
                anonymousReauth: async (data: { userId: string }) => {
                    return $fetch<{
                        token: string
                        user: Record<string, unknown>
                    }>('/sign-in/anonymous-reauth', {
                        method: 'POST',
                        body: data,
                    })
                },
            },
        }),
        pathMethods: {
            '/sign-in/anonymous-reauth': 'POST' as const,
        },
    } satisfies BetterAuthClientPlugin
}

function externalAuthClient() {
    return {
        id: 'external-auth' as const,
        getActions: ($fetch) => ({
            signIn: {
                externalToken: async (data: { token: string }) => {
                    return $fetch<{
                        token: string
                        user: Record<string, unknown>
                    }>('/sign-in/external-token', {
                        method: 'POST',
                        body: data,
                    })
                },
            },
        }),
        pathMethods: {
            '/sign-in/external-token': 'POST' as const,
        },
        atomListeners: [
            {
                matcher: (path: string) => path === '/sign-in/external-token',
                signal: '$sessionSignal' as const,
            },
        ],
    } satisfies BetterAuthClientPlugin
}

type AuthClientOptions = {
    baseURL?: string
    basePath?: string
    plugins: [
        ReturnType<typeof anonymousClient>,
        ReturnType<typeof anonymousReauthClient>,
        ReturnType<typeof externalAuthClient>,
        ReturnType<typeof socialRedirectClient>,
    ]
}

export type AuthClient = ReturnType<typeof createAuthClient<AuthClientOptions>>

export function useChatbotAuth(
    options: {
        baseUrl?: string
        basePath?: string
        externalToken?: MaybeRefOrGetter<string | undefined>
        awaitExternalToken?: boolean
        isActive?: MaybeRefOrGetter<boolean>
        cookieName?: string
    } = {},
) {
    const _token = ref(getSessionCookie(options.cookieName))

    const token = computed({
        get: () => _token.value,
        set: (token: string) => {
            setSessionCookie(token, options.cookieName)
            _token.value = token
        },
    })

    const headers = computed(() =>
        token.value ? { Authorization: `Bearer ${token.value}` } : undefined,
    )

    const client = createAuthClient({
        baseURL: options.baseUrl ?? BACKEND_URL,
        basePath: options.basePath ?? AUTH_BASE_PATH,
        plugins: [
            inferAdditionalFields<typeof auth>(),
            anonymousClient(),
            anonymousReauthClient(),
            externalAuthClient(),
            socialRedirectClient(),
        ],
        fetchOptions: {
            onSuccess: (ctx) => {
                const newTokenFromHeader =
                    ctx.response.headers.get('set-auth-token')
                if (newTokenFromHeader) {
                    token.value = newTokenFromHeader
                }
            },
            auth: {
                type: 'Bearer',
                token: () => token.value,
            },
        },
    }) as AuthClient

    const session = client.useSession()
    const isAuthenticating = ref(false)
    const externalToken = computed(() => toValue(options.externalToken))
    const isActive = computed(() => toValue(options.isActive) ?? true)

    async function signInAnonymously(): Promise<void> {
        const { data } = await client.signIn.anonymous()
        if (data?.token) {
            token.value = data.token
            if (data.user?.id) {
                setAnonymousUserIdCookie(
                    data.user.id as string,
                    options.cookieName,
                )
            }
        }
    }

    /**
     * Attempts to create a new session for an existing anonymous user.
     * Returns true if re-auth succeeded, false otherwise.
     */
    async function tryAnonymousReauth(): Promise<boolean> {
        const storedUserId = getAnonymousUserIdCookie(options.cookieName)
        if (!storedUserId) {
            return false
        }
        try {
            const { data } = await client.signIn.anonymousReauth({
                userId: storedUserId,
            })
            if (data?.token) {
                token.value = data.token
                await session.value.refetch()
                return true
            }
        } catch {
            // user record may have been deleted — fall back to creating a new one
        }
        return false
    }

    async function signInWithExternalToken(
        newExternalToken: string,
    ): Promise<void> {
        if (isAuthenticating.value) {
            return
        }
        isAuthenticating.value = true
        try {
            const { data } = await client.signIn.externalToken({
                token: newExternalToken,
            })
            if (data?.token) {
                token.value = data.token
                await session.value.refetch()
                return
            }
            await signInAnonymously()
        } catch {
            await signInAnonymously()
        } finally {
            isAuthenticating.value = false
        }
    }

    async function signInAnonymouslyWithFallback(): Promise<void> {
        if (isAuthenticating.value) {
            return
        }
        isAuthenticating.value = true
        try {
            const reauthed = await tryAnonymousReauth()
            if (!reauthed) {
                await signInAnonymously()
            }
        } finally {
            isAuthenticating.value = false
        }
    }

    watch(
        [isActive, session],
        ([newIsActive, newSession]) => {
            if (!newIsActive) {
                return
            }
            if (
                !newSession.isPending &&
                !newSession.isRefetching &&
                !isAuthenticating.value &&
                !newSession.data
            ) {
                if (externalToken.value) {
                    signInWithExternalToken(externalToken.value)
                    return
                }
                if (options.awaitExternalToken) {
                    return
                }
                signInAnonymouslyWithFallback()
                return
            }

            // Backfill userId cookie for existing anonymous sessions
            const user = newSession.data?.user
            if (
                user?.isAnonymous &&
                user.id &&
                !getAnonymousUserIdCookie(options.cookieName)
            ) {
                setAnonymousUserIdCookie(user.id as string, options.cookieName)
            }
        },
        { immediate: true },
    )

    watch(externalToken, (newToken) => {
        if (newToken && isActive.value) {
            signInWithExternalToken(newToken)
        }
    })

    function onError(error: { type: string; title: string; status: number }) {
        if (error.status === 401) {
            if (externalToken.value) {
                signInWithExternalToken(externalToken.value)
                return
            }
            signInAnonymouslyWithFallback()
        }
    }

    const userId = computed(() => session.value.data?.user?.id)

    // Auth methods for widget login screen
    async function signInWithEmail(
        email: string,
        password: string,
        rememberMe?: boolean,
    ) {
        const result = await client.signIn.email({
            email,
            password,
            rememberMe,
        })
        // Capture the session token from the response body. Relying solely on
        // the `set-auth-token` header is unreliable here, so we mirror the
        // anonymous/social flows and store the token explicitly.
        if (result.data?.token) {
            token.value = result.data.token
            // Signing in from an anonymous session links (and deletes) the
            // anonymous user server-side, so its stored userId can no longer
            // be re-authenticated — drop it so a later sign-out falls straight
            // back to a fresh anonymous session.
            clearAnonymousUserIdCookie(options.cookieName)
        }
        return result
    }

    function signUpWithEmail(params: {
        email: string
        password: string
        name: string
    }) {
        return client.signUp.email(params)
    }

    function requestPasswordReset(email: string, redirectTo: string) {
        return client.requestPasswordReset({ email, redirectTo })
    }

    /**
     * Opens a popup for social OAuth sign-in.
     *
     * The popup performs a top-level navigation to the backend `social-redirect`
     * endpoint, so the OAuth `state` cookie is set first-party on the backend
     * origin and survives the round-trip (a cross-origin XHR `signIn.social`
     * would set it as a third-party cookie that browsers drop, causing
     * `state_mismatch`). After the flow completes, the backend callback page
     * posts back the session Bearer token, which we store before refetching.
     */
    async function signInWithSocialPopup(
        provider: 'google' | 'microsoft',
        onAuthSuccess?: () => void,
    ): Promise<{ popupBlocked: boolean }> {
        const baseUrl = options.baseUrl ?? BACKEND_URL
        const basePath = options.basePath ?? AUTH_BASE_PATH
        const callbackURL = `${baseUrl}/${basePath}/widget-oauth-callback`
        const redirectUrl = client.signIn.socialRedirect({
            provider,
            callbackURL,
        })

        const popup = window.open(
            redirectUrl,
            'eightwave-oauth',
            'width=600,height=700,menubar=no,toolbar=no',
        )

        if (!popup) {
            return { popupBlocked: true }
        }

        return new Promise((resolve) => {
            const allowedOrigin = new URL(baseUrl).origin
            // Resolve too if the user closes the popup without completing OAuth.
            const closedPollId = setInterval(() => {
                if (popup?.closed) {
                    finish()
                }
            }, 500)

            function finish() {
                window.removeEventListener('message', onOAuthMessage)
                clearInterval(closedPollId)
                resolve({ popupBlocked: false })
            }

            function onOAuthMessage(event: MessageEvent) {
                const isTrustedOAuthMessage =
                    event.origin === allowedOrigin &&
                    event.data?.type === 'eightwave-oauth'
                if (!isTrustedOAuthMessage) {
                    return
                }
                popup?.close()
                if (event.data.token) {
                    token.value = event.data.token
                    // Same as the email flow: the anonymous user was linked
                    // and deleted server-side, its stored userId is stale.
                    clearAnonymousUserIdCookie(options.cookieName)
                }
                session.value.refetch().then(() => onAuthSuccess?.())
                finish()
            }

            window.addEventListener('message', onOAuthMessage)
        })
    }

    /**
     * Signs the current user out and clears the locally stored session token.
     * The `[isActive, session]` watcher above then re-establishes an anonymous
     * session (re-using the stored anonymous userId cookie when present), so the
     * widget keeps working after sign-out.
     */
    async function signOut(): Promise<void> {
        try {
            await client.signOut()
        } finally {
            token.value = ''
            await session.value.refetch()
        }
    }

    return {
        client,
        session,
        isAuthenticating,
        token,
        headers,
        onError,
        userId,
        signInWithEmail,
        signUpWithEmail,
        requestPasswordReset,
        signInSocial: client.signIn.social,
        signInWithSocialPopup,
        signOut,
    }
}
